/**
 * PayPlanModal — step 1 of paying a payment plan (US 4396): choose how much.
 *
 * Defaults to what brings the plan current (past due + next installment). The agent can
 * pay ahead, up to everything currently payable on the plan's batches. On Continue the
 * amount is spread across those batches and handed to the existing BatchPaymentModal
 * (bank account + ACH submit), so plan payments go through exactly the same checkout as
 * Batches to Pay.
 */
import { useEffect, useMemo, useState } from 'react';
import { OathModal, OathButton, OathAlert } from '../../../shared/OathUI';
import { formatCurrencyWithSymbol, formatAmountOnBlur, roundCurrency } from '../../powerreporting/utils';
import { getPlanPayableTotal, getDefaultPaymentAmount } from '../utils/planPayment';
import { formatScheduleDate } from '../utils/scheduleFormat';

const PayPlanModal = ({ isOpen, plan, onClose, onContinue }) => {
  const payable = useMemo(() => getPlanPayableTotal(plan), [plan]);
  const suggested = useMemo(() => getDefaultPaymentAmount(plan), [plan]);
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (isOpen) setAmount(formatAmountOnBlur(suggested));
  }, [isOpen, suggested]);

  if (!plan) return null;

  const numeric = roundCurrency(amount);
  const tooHigh = numeric > payable;
  const invalid = numeric <= 0 || tooHigh;
  const nextDue = roundCurrency(plan.nextDueAmount);
  const isAhead = plan.hasSchedule && numeric > nextDue && nextDue > 0;

  const quickPick = (value) => setAmount(formatAmountOnBlur(value));

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Pay ${plan.name || `${plan.typeName || ''} payment plan`.trim()}`}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <OathButton variant="secondary" onClick={onClose}>Cancel</OathButton>
          <OathButton onClick={() => onContinue(numeric)} disabled={invalid}>
            Continue to payment
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {plan.hasSchedule && nextDue > 0 && (
            <button
              type="button"
              onClick={() => quickPick(Math.min(nextDue, payable))}
              className="text-left p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary"
            >
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {plan.pastDueAmount > 0 ? 'Bring plan current' : `Next payment${plan.nextDueDate ? ` (${formatScheduleDate(plan.nextDueDate)})` : ''}`}
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {formatCurrencyWithSymbol(Math.min(nextDue, payable))}
              </p>
            </button>
          )}
          <button
            type="button"
            onClick={() => quickPick(payable)}
            className="text-left p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary"
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">Pay in full</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(payable)}</p>
          </button>
        </div>

        <div>
          <label htmlFor="pay-plan-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Payment amount
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
            <input
              id="pay-plan-amount"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => setAmount(formatAmountOnBlur(amount))}
              className="w-full pl-7 pr-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {tooHigh && (
          <OathAlert variant="error" message={`You can pay up to ${formatCurrencyWithSymbol(payable)} on this plan right now.`} />
        )}
        {!tooHigh && isAhead && (
          <OathAlert
            variant="info"
            message="Paying more than is due shortens your plan: the extra comes off your last installments."
          />
        )}
      </div>
    </OathModal>
  );
};

export default PayPlanModal;
